import { useState, useEffect } from 'react';
import { Star, ChevronDown, ChevronUp, MoreHorizontal, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

const STORAGE_KEY = 'traderelax_bookmarked_tabs';

// Admin-only tabs - these are hidden from regular users
export const ADMIN_ONLY_TABS = [
  'backtest',    // Backtest
  'recap',       // Recap
];

// Removed/disabled tabs - these are completely hidden from all users
export const REMOVED_TABS = [
  'autotrade',   // Auto Trade - REMOVED
  'paper',       // Paper Trade - REMOVED
  'alpaca',      // Live Trade - REMOVED
  'tools',       // Tools - REMOVED
  'journal',     // Journal - REMOVED
  'discipline',  // Discipline - REMOVED
  'confluence',  // Strong Signals - REMOVED
  'whalealerts', // Whale Alerts - REMOVED
  'polymarket',  // Polymarket - REMOVED
  'smartmoney',  // Smart Money - REMOVED
];

// All available tabs with metadata
// REMOVED: autotrade, paper, alpaca, tools, journal, discipline
export const ALL_TABS = [
  { id: 'dailypicks', label: 'Daily Picks', icon: '📊', category: 'Trading' },
  { id: 'swingtrades', label: 'Swing Trades', icon: '⚡', category: 'Trading' },
  { id: 'breakoutsignals', label: 'Breakout Signals', icon: '🚀', category: 'Trading' },
  { id: 'aisignals', label: 'AI Signals', icon: '🧠', category: 'Trading' },
  { id: 'optionsseller', label: 'Options Seller', icon: '💰', category: 'Trading' },
  { id: 'momentum', label: 'Momentum', icon: '🔥', category: 'Trading' },
  { id: 'pennyrunners', label: 'Penny Runners', icon: '🚀', category: 'Trading' },
  { id: 'amdphases', label: 'AMD Phases', icon: '🎯', category: 'Trading' },
  { id: 'smcpatterns', label: 'SMC Alerts', icon: '⚡', category: 'Trading' },
  { id: 'portfolios', label: 'Portfolios', icon: '📊', category: 'Investing' },
  { id: 'indiamarket', label: 'India Market', icon: '🇮🇳', category: 'Investing' },
  { id: 'fiblevels', label: 'Fib Levels', icon: '🎯', category: 'Trading' },
  { id: 'confluenceanalyzer', label: 'Confluence', icon: '🔬', category: 'Trading' },
  { id: 'setupalerts', label: 'First 15-Min', icon: '⏰', category: 'Trading' },
  { id: 'candlepatterns', label: 'Candle Patterns', icon: '🕯️', category: 'Trading' },
  { id: 'gapscanner', label: 'Gap Scanner', icon: '⚡', category: 'Trading' },
  { id: 'patternbacktest', label: 'Pattern Backtest', icon: '📈', category: 'Analysis' },
  { id: 'combobacktest', label: 'Combo Signals', icon: '🔗', category: 'Analysis' },
  { id: 'weeklywatchlist', label: 'Weekly Watchlist', icon: '📅', category: 'Trading' },
  { id: 'dailycandlewatchlist', label: 'Daily Patterns', icon: '☀️', category: 'Trading' },
  { id: 'catalystscanner', label: 'Catalyst Scanner', icon: '🚀', category: 'Trading' },
  { id: 'breakretest', label: 'Break & Retest', icon: '📊', category: 'Trading' },
  { id: 'congress', label: 'Congress Trades', icon: '🏛️', category: 'Trading' },
  { id: 'tradescore', label: 'Trade Score', icon: '🎯', category: 'Trading' },
  { id: 'sentiment', label: 'Sentiment', icon: '📈', category: 'Analysis' },
  { id: 'analyzer', label: 'Analyzer', icon: '🔍', category: 'Analysis' },
  { id: 'scanner', label: 'Scanner', icon: '📡', category: 'Analysis' },
  { id: 'leaderboard', label: 'Leaderboard', icon: '🏆', category: 'Community' },
  { id: 'backtest', label: 'Backtest', icon: '⏱️', category: 'Analysis' },
  { id: 'alerts', label: 'Alerts', icon: '🔔', category: 'Alerts' },
  { id: 'community', label: 'Community', icon: '💬', category: 'Community' },
  { id: 'calendar', label: 'Calendar', icon: '📅', category: 'Analysis' },
  { id: 'earnings', label: 'Earnings', icon: '💼', category: 'Analysis' },
  { id: 'recap', label: 'Recap', icon: '📋', category: 'Analysis' },
  { id: 'predictions', label: 'Smart Pick', icon: '✨', category: 'Trading' },
  { id: 'sweeps', label: 'Sweeps', icon: '🌊', category: 'Options' },
  { id: 'options', label: 'Options Flow', icon: '📊', category: 'Options' },
  { id: 'vwaprejection', label: 'VWAP Rejection', icon: '📉', category: 'Trading' },
  { id: 'darkpool', label: 'Dark Pool', icon: '🌑', category: 'Analysis' },
  { id: 'insiders', label: 'Insiders', icon: '👔', category: 'Analysis' },
  { id: 'institutional', label: 'Institutional', icon: '🏛️', category: 'Analysis' },
  { id: 'livesignals', label: 'Live Signals', icon: '📡', category: 'Trading' },
  { id: 'learn', label: 'Learn', icon: '📚', category: 'Education' },
  { id: 'settings', label: 'Settings', icon: '⚙️', category: 'System' },
  { id: 'help', label: 'Help', icon: '❓', category: 'System' },
];

// Default bookmarked tabs for new users - exclude admin-only tabs
const DEFAULT_BOOKMARKS = ['dailypicks', 'swingtrades', 'aisignals', 'tradescore', 'livesignals', 'alerts'];

// Helper function to filter tabs based on admin status
export const getVisibleTabs = (isAdmin) => {
  // First filter out completely removed tabs
  let visibleTabs = ALL_TABS.filter(tab => !REMOVED_TABS.includes(tab.id));
  
  // Then filter admin-only tabs for non-admin users
  if (!isAdmin) {
    visibleTabs = visibleTabs.filter(tab => !ADMIN_ONLY_TABS.includes(tab.id));
  }
  
  return visibleTabs;
};

export function useBookmarkedTabs(isAdmin = false) {
  const [bookmarkedTabs, setBookmarkedTabs] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : DEFAULT_BOOKMARKS;
    } catch {
      return DEFAULT_BOOKMARKS;
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(bookmarkedTabs));
  }, [bookmarkedTabs]);

  const toggleBookmark = (tabId) => {
    setBookmarkedTabs(prev => {
      if (prev.includes(tabId)) {
        return prev.filter(id => id !== tabId);
      }
      return [...prev, tabId];
    });
  };

  const isBookmarked = (tabId) => bookmarkedTabs.includes(tabId);

  const getBookmarkedTabsData = () => {
    const visibleTabs = getVisibleTabs(isAdmin);
    return bookmarkedTabs
      .map(id => visibleTabs.find(tab => tab.id === id))
      .filter(Boolean);
  };

  const getNonBookmarkedTabs = () => {
    const visibleTabs = getVisibleTabs(isAdmin);
    return visibleTabs.filter(tab => !bookmarkedTabs.includes(tab.id));
  };

  return {
    bookmarkedTabs,
    toggleBookmark,
    isBookmarked,
    getBookmarkedTabsData,
    getNonBookmarkedTabs,
    isAdmin,
  };
}

// Tab Bookmark Star Button - uses span with role to avoid button nesting issues
export function TabBookmarkButton({ tabId, isBookmarked, onToggle, size = 'sm' }) {
  return (
    <span
      role="button"
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation();
        onToggle(tabId);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          onToggle(tabId);
        }
      }}
      className={`p-1 rounded transition-colors cursor-pointer ${
        isBookmarked 
          ? 'text-amber-400 hover:text-amber-300' 
          : 'text-zinc-600 hover:text-zinc-400'
      }`}
      title={isBookmarked ? 'Remove from favorites' : 'Add to favorites'}
    >
      <Star className={`${size === 'sm' ? 'w-3 h-3' : 'w-4 h-4'} ${isBookmarked ? 'fill-current' : ''}`} />
    </span>
  );
}

// More Tabs Dropdown with Bookmark functionality
export function MoreTabsDropdown({ tabs, onSelect, currentTab, onBookmark }) {
  const [isOpen, setIsOpen] = useState(false);
  
  // Group tabs by category
  const groupedTabs = tabs.reduce((acc, tab) => {
    if (!acc[tab.category]) acc[tab.category] = [];
    acc[tab.category].push(tab);
    return acc;
  }, {});

  if (tabs.length === 0) return null;

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="text-zinc-400 hover:text-white hover:bg-zinc-800 gap-1"
        >
          <MoreHorizontal className="w-4 h-4" />
          <span className="text-xs">More</span>
          <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-72 bg-zinc-900 border-zinc-700 max-h-96 overflow-y-auto"
      >
        <div className="px-3 py-2 border-b border-zinc-700 bg-zinc-800/50">
          <p className="text-xs text-amber-400 font-medium">Tap ⭐ to add tab to favorites</p>
        </div>
        {Object.entries(groupedTabs).map(([category, categoryTabs], idx) => (
          <div key={category}>
            {idx > 0 && <DropdownMenuSeparator className="bg-zinc-700" />}
            <div className="px-3 py-1.5 text-xs font-semibold text-zinc-500 uppercase tracking-wide">
              {category}
            </div>
            {categoryTabs.map(tab => (
              <div
                key={tab.id}
                className={`flex items-center justify-between px-2 py-2 mx-1 hover:bg-zinc-800 rounded-lg cursor-pointer ${
                  currentTab === tab.id ? 'bg-zinc-800 text-cyan-400' : 'text-zinc-300'
                }`}
              >
                <div 
                  className="flex items-center flex-1 min-w-0"
                  onClick={() => {
                    onSelect(tab.id);
                    setIsOpen(false);
                  }}
                >
                  <span className="mr-2 text-base">{tab.icon}</span>
                  <span className="text-sm truncate">{tab.label}</span>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onBookmark) onBookmark(tab.id);
                  }}
                  className="flex-shrink-0 ml-auto p-2 rounded-full bg-amber-500/10 border border-amber-500/30 hover:bg-amber-500/30 text-amber-400 hover:text-amber-300 transition-all active:scale-95"
                  title="Add to favorites"
                >
                  <Star className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default useBookmarkedTabs;
